import { Basic } from "../basic";
import { markStore, MarkStore, marksData } from "@lib/store/src/mark";
import { MarkModal } from "./modals/mark-modal";
import { restoreImagesFromStore, setupDragAndDrop } from "./utils/image-tools";
import { showMarkControlContextMenu } from "./utils/context-menu-mark";
import styles from "./modals/mark-modals.styles.css";
import { setupClickOutsideHandler } from "./utils/event-handlers";
import { reaction } from "mobx";
import i18next from "i18next";

function toMarkDataRow(img: any, controlId: string, id: number) {
  return {
    Id: id,
    Nazvanie: String(img.id ?? ""),
    Prioritet: img.priority ?? 0,
    X: img.x ?? img.left ?? 0,
    Y: img.y ?? img.top ?? 0,
    Operator: img.commentAuthor ?? "",
    Kommentarij: img.comment ?? "",
    Id_elementa: Number(img.linkedElementId) || 0,
    Vidimostj: img.hidden !== true,
    src: img.src ?? "",
    width: img.width ?? 0,
    height: img.height ?? 0,
    windowId: img.windowId != null ? String(img.windowId) : "",
    controlId,
    linkedElementId: img.linkedElementId != null ? String(img.linkedElementId) : "",
    propertysubtypes: img.propertysubtypes ?? "",
    setvalue: img.setvalue ?? "",
    left: img.left ?? img.x ?? 0,
    top: img.top ?? img.y ?? 0,
    timestamp: img.timestamp ?? 0,
    group: img.group ?? "",
    origin: img.origin ?? "",
    linkSide: img.linkSide ?? "",
    targetSide: img.targetSide ?? "",
    commentDate: img.commentDate ?? "",
  };
}

function toServerMarks(store: MarkStore) {
  return (store.droppedImages || []).map((img, i) => toMarkDataRow(img, store.id, i + 1));
}

function rowToDroppedImage(r: any, controlId: string) {
  const x = Number(r.X ?? r.left ?? 0);
  const y = Number(r.Y ?? r.top ?? 0);
  return {
    id: String(r.Nazvanie || ""),
    src: r.src ?? "",
    x,
    y,
    left: Number(r.left ?? x),
    top: Number(r.top ?? y),
    priority: Number(r.Prioritet ?? 0),
    width: Number(r.width ?? 0),
    height: Number(r.height ?? 0),
    timestamp: Number(r.timestamp ?? 0),
    windowId: r.windowId != null ? String(r.windowId) : "",
    controlId: String(r.controlId || controlId || ""),
    linkedElementId:
      r.linkedElementId != null && String(r.linkedElementId) !== ""
        ? String(r.linkedElementId)
        : r.Id_elementa
          ? String(r.Id_elementa)
          : undefined,
    propertysubtypes: r.propertysubtypes || undefined,
    setvalue: r.setvalue || undefined,
    group: r.group || undefined,
    origin: r.origin || undefined,
    linkSide: r.linkSide || undefined,
    targetSide: r.targetSide || undefined,
    comment: r.Kommentarij ?? "",
    commentAuthor: r.Operator ?? "",
    commentDate: r.commentDate ?? "",
    hidden: r.Vidimostj === false,
  };
}

function fromServerMarks(value: any, store: MarkStore) {
  const data = value?.value ?? value;
  if (!Array.isArray(data)) return false;

  const droppedImages: any[] = [];
  const seen = new Set<string>();
  for (const r of data) {
    if (!r?.src || r.isDeleted) continue;
    const img = rowToDroppedImage(r, store.id);
    if (img.id && seen.has(img.id)) continue;
    if (img.id) seen.add(img.id);
    droppedImages.push(img);
  }

  store.hydrateFromJSON({ droppedImages });
  return true;
}

export class MSMarkControl extends Basic {
  private static _modal: MarkModal | null = null;

  static get observedAttributes() {
    return super.observedAttributes.concat(["connectionlinecolor","selectionoutlinecolor","marksdata", "marks", "x", "y", "fontsize"]);
  }

  constructor() {
    super();
    this._restoredOnce = false;
  }
  _connectionlinecolor: string = "#216aff";
  _selectionoutlinecolor: string = "#216aff";
  _marksdata: any;
  markStores?: MarkStore;
  disposer: any;
  _restoredOnce: boolean;
  _applying = false;
  _x: any;
  _y: any;
  _fontsize: any;
  
  get x() {
    return this._x;
  }

  set x(value) {
    this._x = value
    this.style.left = this._x;
  }

  get y() {
    return this._y;
  }

  set y(value) {
    this._y = value;
    this.style.top = this._y;
  }

  get fontsize() {
    return this._fontsize;
  }

  set fontsize(value) {
    this._fontsize = value;
    this.style.fontSize = this._fontsize;;
  }

  get marksdata () {
    return this._marksdata;
  }
  
  set marksdata(value) {
    this._marksdata = value;
    if (!this.markStores) return;

    this._applying = true;
    const applied = fromServerMarks(value, this.markStores);
    this._applying = false;
    if (!applied) return;

    if (!this._restoredOnce) {
      setTimeout(() => restoreImagesFromStore(this), 0);
      this._restoredOnce = true;
    }
  }

  get connectionlinecolor() {
    return this._connectionlinecolor;
  }


  set connectionlinecolor(value: string) {
    this._connectionlinecolor = value;
  }

  get selectionoutlinecolor() {
    return this._selectionoutlinecolor;
  }

  set selectionoutlinecolor(value: string) {
    this._selectionoutlinecolor = value;
  }

  connectedCallback() {
    super.connectedCallback?.();
    const styleTag = document.createElement("style");
    styleTag.textContent = styles;
    document.head.appendChild(styleTag);
    this.style.position = "absolute";
    this.style.display = "inline-flex";
    this.style.alignItems = "center";
    this.style.justifyContent = "center";

    const label = document.createElement("span");
    label.textContent = `${i18next.t("marks")}`
    this.appendChild(label);
    this.addEventListener("contextmenu", this._contextMenuHandler);

    this.markStores = new MarkStore();
    this.markStores.id = this.getAttribute("id");
    marksData.marksData.push(this.markStores)
    this.onStoreChange()

    this.markStores.connectionlinecolor = this._connectionlinecolor;
    this.markStores.selectionoutlinecolor = this._selectionoutlinecolor;

    setupClickOutsideHandler();
    if (this._marksdata != null) {
      this.marksdata = this._marksdata;
    }
    this.setupClickHandler();
    setupDragAndDrop(this);

  }

  private _contextMenuHandler = (e: MouseEvent) => {
    e.preventDefault();
    const id = this.getAttribute("id");
    document.querySelectorAll(".ms-markcontrol").forEach(c => c.classList.remove("opened"));
    this.classList.add("opened");
    if (id) showMarkControlContextMenu(e, id);
  }

  private setupClickHandler() {
    const marksAttr = this.getAttribute("marks");
    const postersAttr = this.getAttribute("posters");
    const notesAttr = this.getAttribute("notes");
    let posters: any[] = [];
    let notes: any[] = [];

    if (marksAttr) {
      const parsed = JSON.parse(marksAttr);
      posters = Array.isArray(parsed.mark) ? parsed.mark : [parsed.mark];
    } else if (postersAttr) {
      const postersParsed = JSON.parse(postersAttr);
      posters = Array.isArray(postersParsed.mark) ? postersParsed.mark : [postersParsed.mark];

      if (notesAttr) {
        const notesParsed = JSON.parse(notesAttr);
        notes = Array.isArray(notesParsed.mark) ? notesParsed.mark : [notesParsed.mark];
      }
    }
    
    this.classList.add("ms-markcontrol");
    const isMark = marksAttr;
    this.textContent = isMark ? `${i18next.t("marks")}` : `${i18next.t("posters")}`;
    if(marksAttr == null && postersAttr == null) this.textContent = `${i18next.t("marks")}`;
    
    this.onclick = () => {
      if (!MSMarkControl._modal) {
        MSMarkControl._modal = new MarkModal("mark-popup");
        MSMarkControl._modal.initObserver();
      }
      markStore.isMarkControl = this.textContent === `${i18next.t("marks")}`;
      document.querySelectorAll(".ms-markcontrol").forEach(c => c.classList.remove("opened"));
      this.classList.add("opened");
      markStore.openModal(posters, notes); 
    };
  }

  onStoreChange() {
    this.disposer = reaction(
      () => JSON.stringify(this.markStores?.toJSON()),
      () => {
        if (this._applying || !this.markStores) return;
        const payload = toServerMarks(this.markStores);
        if (!this._restoredOnce && payload.length === 0) return;
        this.SetParameter("marksdata", payload, null, null, null, null , null);
      }
    );
  }
  
  disconnectedCallback(): void {
    this.removeEventListener("contextmenu" , this._contextMenuHandler);
    this.disposer?.();
    this.disposer = undefined;
    const idx = marksData.marksData.indexOf(this.markStores);
    if (idx >= 0) marksData.marksData.splice(idx, 1);
    super.disconnectedCallback?.();
  }
}
